import { Bell, Check, Download, KeyRound, Languages, ListChecks, LogOut } from "lucide-react";
import { FormEvent, useState } from "react";
import logo from "../../assets/brand/doctorsmeal-logo.png";
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type Language } from "../shared/localization";
import { normalizeAvailableDays } from "../shared/menu-availability";
import type { ActiveOnboardingStep, OnboardingStep } from "../shared/onboarding";
import { api, json } from "./api";
import { useI18n } from "./i18n";
import type { InstallResult } from "./install";
import { enablePushNotifications } from "./push";
import type { TranslationKey } from "./translations";
import type { User } from "./types";

type StepResult = {
  onboardingStep: OnboardingStep;
  language?: Language;
  mustChangePassword?: boolean;
  mealReminderEnabled?: boolean;
  workDays?: number[];
  viewGuide?: boolean;
};

const stepCopy: Record<
  ActiveOnboardingStep,
  { eyebrow: TranslationKey; title: TranslationKey; description: TranslationKey }
> = {
  1: { eyebrow: "onboarding.language.eyebrow", title: "onboarding.language.title", description: "onboarding.language.description" },
  2: { eyebrow: "onboarding.password.eyebrow", title: "onboarding.password.title", description: "onboarding.password.description" },
  3: { eyebrow: "onboarding.reminder.eyebrow", title: "onboarding.reminder.title", description: "onboarding.reminder.description" },
  4: { eyebrow: "onboarding.workdays.eyebrow", title: "onboarding.workdays.title", description: "onboarding.workdays.description" },
  5: { eyebrow: "onboarding.install.eyebrow", title: "onboarding.install.title", description: "onboarding.install.description" },
  6: { eyebrow: "onboarding.guide.eyebrow", title: "onboarding.guide.title", description: "onboarding.guide.description" },
};

export function Onboarding({
  user,
  onUser,
  onComplete,
  canInstall,
  requestInstall,
  onLogout,
}: {
  user: User & { language: Language; onboardingStep: OnboardingStep };
  onUser: (user: User) => void;
  onComplete?: (viewGuide: boolean) => void;
  canInstall: boolean;
  requestInstall: () => Promise<InstallResult>;
  onLogout: () => Promise<void>;
}) {
  const { t, errorMessage } = useI18n();
  const step = Math.min(user.onboardingStep, 6) as ActiveOnboardingStep;
  const copy = stepCopy[step];
  const [language, setLanguage] = useState(user.language);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [reminderEnabled, setReminderEnabled] = useState<boolean | null>(
    user.mealReminderEnabled ?? null,
  );
  const [workDays, setWorkDays] = useState<number[]>(
    user.workDays?.length ? normalizeAvailableDays(user.workDays) : [1, 2, 3, 4, 5],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit<T extends StepResult>(
    path: string,
    method: "POST" | "PUT",
    body: unknown,
  ) {
    setBusy(true);
    setError("");
    try {
      const result = await api<T>(path, json(method, body));
      onUser({
        ...user,
        ...result,
        language: result.language ?? user.language,
        onboardingStep: result.onboardingStep,
      });
      return result;
    } catch (problem) {
      setError(errorMessage(problem));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function saveLanguage(event: FormEvent) {
    event.preventDefault();
    await submit<StepResult>("/api/onboarding/language", "PUT", { language });
  }

  async function savePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t("error.PASSWORD_MISMATCH"));
      return;
    }
    if (newPassword.length < 4) {
      setError(t("error.PASSWORD_TOO_SHORT"));
      return;
    }
    await submit<StepResult>("/api/onboarding/password", "POST", { newPassword });
  }

  async function saveReminder(event: FormEvent) {
    event.preventDefault();
    if (reminderEnabled === null) {
      setError(t("error.REMINDER_CHOICE_REQUIRED"));
      return;
    }
    const pushRegistration = reminderEnabled
      ? enablePushNotifications().catch(() => "disabled" as const)
      : Promise.resolve("disabled" as const);
    await submit<StepResult>("/api/onboarding/reminder", "PUT", { enabled: reminderEnabled });
    await pushRegistration;
  }

  async function saveWorkDays(event: FormEvent) {
    event.preventDefault();
    if (!workDays.length) {
      setError(t("error.INVALID_WORK_DAYS"));
      return;
    }
    await submit<StepResult>("/api/onboarding/work-days", "PUT", { workDays });
  }

  async function finishInstall(action: "installed" | "dismissed" | "skipped") {
    await submit<StepResult>("/api/onboarding/install", "POST", { action });
  }

  async function installApp() {
    setBusy(true);
    setError("");
    try {
      const result = await requestInstall();
      await finishInstall(result);
    } catch (problem) {
      setError(errorMessage(problem));
      setBusy(false);
    }
  }

  async function finishGuide(viewGuide: boolean) {
    const result = await submit<StepResult>("/api/onboarding/guide", "POST", { viewGuide });
    if (result) onComplete?.(viewGuide);
  }

  return (
    <div className="onboarding-shell">
      <header className="topbar onboarding-topbar">
        <div className="brand brand--compact">
          <img src={logo} alt="닥터스" />
          <strong>닥터스</strong>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label={t("common.logout")}
          onClick={() => void onLogout()}
        >
          <LogOut size={19} />
        </button>
      </header>
      <main className="auth-page onboarding-page">
        <section className="auth-card onboarding-card">
          <div className="onboarding-progress-meta">
            <span>{t("onboarding.label")}</span>
            <strong>{step} / 6</strong>
          </div>
          <div
            className="onboarding-progress"
            role="progressbar"
            aria-label={t("onboarding.label")}
            aria-valuemin={1}
            aria-valuemax={6}
            aria-valuenow={step}
          >
            <span style={{ width: `${(step / 6) * 100}%` }} />
          </div>
          <div className="auth-copy onboarding-copy">
            <span>{t(copy.eyebrow)}</span>
            <h1>{t(copy.title)}</h1>
            <p>{t(copy.description)}</p>
          </div>

        {step === 1 && (
          <form className="form-stack" onSubmit={saveLanguage}>
            <div className="language-choices" role="radiogroup" aria-label={t("onboarding.language.eyebrow")}>
              {SUPPORTED_LANGUAGES.map((value) => (
                <label key={value} className={language === value ? "is-selected" : ""}>
                  <input type="radio" name="language" value={value} checked={language === value} onChange={() => setLanguage(value)} />
                  <span className="language-symbol">{value === "ko" ? "한" : value === "en" ? "EN" : value === "zh-CN" ? "简" : "繁"}</span>
                  <span><strong>{LANGUAGE_LABELS[value].native}</strong><small>{LANGUAGE_LABELS[value].korean}</small></span>
                  <Check className="choice-check" />
                </label>
              ))}
            </div>
            {error && <p className="form-error">{error}</p>}
            <div className="onboarding-actions">
              <button className="primary-button" disabled={busy}>{t("common.next")}</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className="form-stack" onSubmit={savePassword}>
            <label>{t("onboarding.password.new")}<input type="password" autoComplete="new-password" minLength={4} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /></label>
            <label>{t("onboarding.password.confirm")}<input type="password" autoComplete="new-password" minLength={4} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></label>
            {error && <p className="form-error">{error}</p>}
            <div className="onboarding-actions">
              <button className="primary-button" disabled={busy}>{t("onboarding.password.action")}</button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form className="form-stack" onSubmit={saveReminder}>
            <div className="onboarding-info-card"><Bell /><div><strong>{t("onboarding.reminder.changeTitle")}</strong><p>{t("onboarding.reminder.changeDescription")}</p></div></div>
            <div className="binary-choices" role="radiogroup" aria-label={t("onboarding.reminder.eyebrow")}>
              <label className={reminderEnabled === true ? "is-selected" : ""}><input type="radio" name="reminder" checked={reminderEnabled === true} onChange={() => setReminderEnabled(true)} />{t("common.yes")}</label>
              <label className={reminderEnabled === false ? "is-selected" : ""}><input type="radio" name="reminder" checked={reminderEnabled === false} onChange={() => setReminderEnabled(false)} />{t("common.no")}</label>
            </div>
            {error && <p className="form-error">{error}</p>}
            <div className="onboarding-actions">
              <button className="primary-button" disabled={busy}>{t("onboarding.reminder.action")}</button>
            </div>
          </form>
        )}

        {step === 4 && (
          <form className="form-stack" onSubmit={saveWorkDays}>
            <fieldset className="onboarding-weekdays">
              <legend>{t("onboarding.workdays.eyebrow")}</legend>
              {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                <label key={day} className={workDays.includes(day) ? "is-selected" : ""}>
                  <input type="checkbox" checked={workDays.includes(day)} onChange={() => setWorkDays((current) => current.includes(day) ? current.filter((value) => value !== day) : normalizeAvailableDays([...current, day]))} aria-label={t(`weekday.${day}.long` as TranslationKey)} />
                  <span>{t(`weekday.${day}.short` as TranslationKey)}</span>
                </label>
              ))}
            </fieldset>
            {error && <p className="form-error">{error}</p>}
            <div className="onboarding-actions">
              <button className="primary-button" disabled={busy}>{t("onboarding.workdays.action")}</button>
            </div>
          </form>
        )}

        {step === 5 && (
          <div className="form-stack">
            <div className="onboarding-install-hero"><img src={logo} alt="" /><span>{t("onboarding.install.caption")}</span></div>
            {error && <p className="form-error">{error}</p>}
            <div className="onboarding-actions">
              <button className="primary-button" disabled={busy || !canInstall} onClick={() => void installApp()}><Download />{t("onboarding.install.action")}</button>
              <button className="outline-button onboarding-secondary" disabled={busy} onClick={() => void finishInstall("skipped")}>{t("common.skip")}</button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="form-stack">
            <div className="onboarding-guide-preview">
              <div><ListChecks /><span><strong>{t("guide.meals.title")}</strong><small>{t("guide.meals.description")}</small></span></div>
              <div><KeyRound /><span><strong>{t("guide.deadline.title")}</strong><small>{t("guide.deadline.description")}</small></span></div>
              <div><Languages /><span><strong>{t("profile.language.title")}</strong><small>{t("profile.language.description")}</small></span></div>
            </div>
            {error && <p className="form-error">{error}</p>}
            <div className="onboarding-actions">
              <button className="primary-button" disabled={busy} onClick={() => void finishGuide(true)}>{t("onboarding.guide.action")}</button>
              <button className="outline-button onboarding-secondary" disabled={busy} onClick={() => void finishGuide(false)}>{t("onboarding.guide.skip")}</button>
            </div>
          </div>
        )}
        </section>
      </main>
    </div>
  );
}
